import type { JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { Icon } from '../design-system/icon.tsx'
import { nextPreference } from '../lib/theme.ts'
import { useTheme } from './theme-provider.tsx'

export interface ThemeToggleProps {
  'data-testid': string
  /**
   * Extra classes for the surface this sits on.
   *
   * The control is surface-agnostic on purpose. On the navy rail the caller
   * passes the sidebar's own foreground, hover and ring, exactly as the mobile
   * menu button does — the house focus ring is a light-surface ring and is
   * under the 3:1 floor on navy.
   */
  className?: string
}

/**
 * ThemeToggle — switch the console between light and dark.
 *
 * One press flips what is on screen and holds the result, so a reader who has
 * never pressed it keeps following the OS setting. `src/lib/theme.ts` carries
 * that rule; this is only the control.
 *
 * The glyph names the theme a press switches TO rather than the one showing.
 * A control that pictures its current state and a control that pictures its
 * action look identical in a screenshot and mean opposite things, so the
 * `aria-label` says the action outright and the glyph agrees with the label.
 *
 * @param props - Toggle props.
 * @param props.data-testid - Test hook.
 * @param props.className - Extra classes for the surface it sits on.
 * @returns The rendered control.
 */
export function ThemeToggle({
  'data-testid': testId,
  className
}: ThemeToggleProps): JSX.Element {
  const { theme, toggle } = useTheme()
  const target = nextPreference(theme)

  return (
    <Button
      data-testid={testId}
      variant="quiet"
      size="icon"
      aria-label={`Switch to ${target} theme`}
      // The pressed state is the honest reading of a two-state control, and it
      // is what a screen reader announces on the press itself. Without it the
      // label changes under the reader with nothing said.
      aria-pressed={theme === 'dark'}
      className={className}
      onClick={toggle}
    >
      <Icon name={target === 'dark' ? 'moon' : 'sun'} />
    </Button>
  )
}
